// The one slot endpoint with no skipInvalidate knob.
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'
import { collectRouteSlotIds } from '../slotPayloads'

import type { SlotRow } from '../slotPayloads'
import type { StepResult } from '../types'
import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'
import type { DayOfWeek, RouteId } from 'types/entities/shared/ids'
import type { Deps, PipelineStep } from 'types/modules/routing'

export interface ChangeDayMasterSlotStepArgs {
  readonly routeId: RouteId
  readonly toDayOfWeek: DayOfWeek
  readonly rows: ReadonlyArray<SlotRow>
  readonly futureStops?: boolean
}

export type ChangeDayMasterSlotStepValue = { readonly stopCount: number }

export type ChangeDayMasterSlotStepResult = StepResult<'CHANGE_DAY_MASTER', ChangeDayMasterSlotStepValue>

export const makeChangeDayMasterSlotStep = (
  args: ChangeDayMasterSlotStepArgs,
  deps: Deps
): PipelineStep<'CHANGE_DAY_MASTER', ChangeDayMasterSlotStepResult> => {
  const stepId = `change-day-master-slot:${args.routeId}:${args.toDayOfWeek}`
  return {
    id: stepId,
    phase: 'CHANGE_DAY_MASTER',
    label: 'Failed to change the master day of week',
    applyOrder: 100,
    run: async () => {
      if (!deps.changeDayMasterSlot) {
        return err({ kind: 'PRECONDITION_FAILED', reason: 'changeDayMasterSlot dep not provided — the v3-slot strategy requires it' })
      }
      const routeSlotIds = collectRouteSlotIds(args.rows)
      if (routeSlotIds.length === 0) {
        return ok({ phase: 'CHANGE_DAY_MASTER', stepId, value: { stopCount: 0 } })
      }

      try {
        const response = await deps
          .changeDayMasterSlot({
            routeId: args.routeId,
            data: { dayOfWeek: args.toDayOfWeek as ApiDayOfWeek, futureStops: args.futureStops ?? true, stops: [...routeSlotIds] },
          })
          .unwrap()
        return ok({ phase: 'CHANGE_DAY_MASTER', stepId, value: { stopCount: response?.stopCount ?? 0 } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({ kind: 'BE_REJECTED', phase: 'CHANGE_DAY_MASTER', status, message })
      }
    },
  }
}
